import { useState } from 'react';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useSiteContent } from '../context/SiteContentContext';
import { saveStoreSettings } from '../lib/storeData';
import { showToast } from '../components/ui/Toast';

export default function AdminStoreStatus() {
  const { loading } = useSiteContent();
  if (loading) return <p className="adm-sub">Loading store settings...</p>;
  return <StoreStatusForm />;
}

function StoreStatusForm() {
  const { content, updateBulk } = useSiteContent();
  const [form, setForm] = useState({ ...content });
  const [saved, setSaved] = useState(true);

  function set(key, value) { setForm(prev => ({ ...prev, [key]: value })); setSaved(false); }
  async function save() {
    const threshold = form.free_shipping_threshold;
    if (threshold === '' || threshold === undefined || isNaN(Number(threshold)) || Number(threshold) < 0) {
      showToast('Free shipping threshold must be a number (0 or more).', 'error');
      return;
    }
    const result = await saveStoreSettings({
      store_status: form.store_status,
      free_shipping_threshold: Number(threshold),
      pause_reopen_date: form.store_pause_reopen || null
    });
    if (result.error) {
      showToast(result.error, 'error');
      return;
    }
    // The pause message is site text (site_content); the rest went to store_settings above.
    const textResult = await updateBulk(form);
    if (textResult && textResult.error) {
      showToast(textResult.error, 'error');
      return;
    }
    setSaved(true);
    showToast('Store settings saved');
  }

  const status = form.store_status || 'open';

  return (
    <div>
      <h1 className="adm-title">Store status</h1>
      <p className="adm-sub">Control whether customers can place orders.</p>

      <div className="adm-card">
        <div className="adm-card-title">Current status</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['open', 'paused', 'closed'].map(s => (
            <button
              key={s}
              className={`adm-btn ${status === s ? '' : 'adm-btn--outline'}`}
              onClick={() => set('store_status', s)}
              style={{ textTransform: 'capitalize' }}
            >
              {s === 'open' && '✓ '}{s}
            </button>
          ))}
        </div>
      </div>

      {status === 'paused' && (
        <div className="adm-card">
          <div className="adm-card-title">Pause settings</div>
          <div className="adm-field">
            <label className="adm-label">Banner message (shown on every page)</label>
            <textarea className="adm-textarea" value={form.store_pause_message || ''} onChange={e => set('store_pause_message', e.target.value)} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Auto-reopen date (optional)</label>
            <input className="adm-input" type="date" value={form.store_pause_reopen || ''} onChange={e => set('store_pause_reopen', e.target.value)} />
          </div>
        </div>
      )}

      <div className="adm-card">
        <div className="adm-card-title">Lead times</div>
        <div className="adm-row2">
          <div className="adm-field">
            <label className="adm-label">Standard lead time</label>
            <input className="adm-input" value={form.lead_time_standard || ''} onChange={e => set('lead_time_standard', e.target.value)} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Busy period lead time</label>
            <input className="adm-input" value={form.lead_time_busy || ''} onChange={e => set('lead_time_busy', e.target.value)} />
          </div>
        </div>
        <div className="adm-field">
          <label className="adm-label">Free shipping threshold ($)</label>
          <input className="adm-input" value={form.free_shipping_threshold || ''} onChange={e => set('free_shipping_threshold', e.target.value)} />
        </div>
      </div>

      <div className="adm-card">
        <div className="adm-card-title">Error pages</div>
        <div className="adm-field">
          <label className="adm-label">404 heading</label>
          <input className="adm-input" value={form.error_404_heading || ''} onChange={e => set('error_404_heading', e.target.value)} />
        </div>
        <div className="adm-field">
          <label className="adm-label">404 message</label>
          <textarea className="adm-textarea" value={form.error_404_body || ''} onChange={e => set('error_404_body', e.target.value)} />
        </div>
        <div className="adm-field">
          <label className="adm-label">500 heading</label>
          <input className="adm-input" value={form.error_500_heading || ''} onChange={e => set('error_500_heading', e.target.value)} />
        </div>
        <div className="adm-field">
          <label className="adm-label">500 message</label>
          <textarea className="adm-textarea" value={form.error_500_body || ''} onChange={e => set('error_500_body', e.target.value)} />
        </div>
      </div>

      <div className="adm-save-bar">
        <span className={`adm-save-status ${saved ? 'adm-save-status--saved' : ''}`}>
          {saved ? '✓ Saved' : '● Unsaved'}
        </span>
        <button className="adm-btn" onClick={save}><IconDeviceFloppy size={14} /> Save</button>
      </div>
    </div>
  );
}
